<template>
	<div class="row room-overview">
		<div
			v-for="room in items"
			:key="room.ID"
			class="room filter-item"
			:class="{ active: filter === room.type || filter === 'showAll' }"
		>
			<div class="row">
				<div class="columns column6 room-image">
					<div v-if="room.images.length">
						<a
							:data-src="room.images[0].image"
							:data-fancybox="`gallery-${room.ID}`"
							:data-caption="room.images[0].imageAlt"
							:title="room.images[0].imageAlt"
						>
							<picture>
								<source
									v-if="room.images[0].imagesWebp[1]"
									:srcset="room.images[0].imagesWebp[1]"
									type="image/webp"
								/>
								<source :srcset="room.images[0].images[1]" />
								<img :src="room.images[0].images[1]" :alt="room.images[0].imageAlt" loading="lazy" />
							</picture>
						</a>
					</div>
					<div v-if="room.images.slice(1).length" class="thumbnails">
						<a
							v-for="item in room.images.slice(1)"
							:key="item.image"
							:data-src="item.image"
							:data-fancybox="`gallery-${room.ID}`"
							:data-caption="item.imageAlt"
							:title="item.imageAlt"
						>
							<picture>
								<source v-if="item.imagesWebp" :srcset="item.imagesWebp[1]" type="image/webp" />
								<source :srcset="item.images[1]" />
								<img :src="item.images[1]" />
							</picture>
						</a>
					</div>
				</div>
				<div class="columns column6 room-content">
					<h2>{{ room.header }}</h2>
					<div v-parse-links v-html="room.content" />
					<div class="columns column12 details-bar">
						<div class="icons-group">
							<span><font-awesome-icon icon="fa fa-users" size="1x" />{{ room.pax }}</span>
							<span><font-awesome-icon icon="fa fa-arrows" size="1x" />{{ room.surface }}</span>
							<span v-if="room.price">
								<font-awesome-icon icon="fa fa-euro-sign" size="1x" />{{ room.price }}
							</span>
						</div>

						<book-button v-if="type === 'rooms'" class="button cta">
							{{ $t('bookThisRoom') }}
						</book-button>

						<nuxt-link v-else :to="localePath('meetingform', locale)" class="button cta">
							{{ $t('buttonRequestInfo') }}
						</nuxt-link>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

defineProps({
	items: { type: Array, default: () => [] },
	filter: { type: String, default: 'showAll' },
	type: { type: String, default: 'rooms' },
});

onMounted(() => {
	Fancybox.bind('[data-fancybox]', {});
});
</script>

<style lang="scss" scoped>
.room-overview {
	padding: 0 20px;
}

.room {
	display: flex;
	flex-direction: column;
	padding-bottom: 35px;
	width: 100%;

	&.active {
		display: block;
	}

	.row {
		display: flex;
		align-items: stretch;

		.room-image {
			display: flex;
			flex-direction: column;
			padding: 0;
			align-items: stretch;

			> div:first-child {
				flex: 1;
				display: flex;
				height: auto;
				min-height: 100%;
			}

			a {
				flex: 1;
				display: flex;
				width: 100%;
				height: 100%;
			}

			img,
			picture {
				flex: 1;
				width: 100%;
				height: 100%;
				object-fit: cover;
				display: block;
			}

			.thumbnails {
				display: flex;
				gap: 5px;

				img {
					max-width: 100px;
				}
			}
		}

		.room-content {
			flex: 1 1 50%;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			padding: 35px;
			background: var(--primary-background-color);
		}

		.border-column {
			padding: 0 30px;
		}
	}

	.border {
		background: #fbf2f7;
		margin: 10px 0 0;
		height: 1px;
	}

	.details-bar {
		padding: 0;
		display: flex;

		.icons-group {
			display: flex;
			align-items: center;
		}

		svg {
			margin-right: 5px;
		}

		.icons-group span {
			margin-right: 25px;
		}

		.cta {
			margin-left: auto;
			padding: 15px 25px;
		}
	}
}

@media (max-width: 680px) {
	.room-overview {
		padding: 0 20px;
	}

	.room {
		.row {
			flex-direction: column;

			.room-image,
			.room-content {
				width: 100%;
			}
		}

		.details-bar {
			flex-direction: column;
			align-items: flex-start;

			.icons-group {
				align-items: flex-start;

				span {
					margin-bottom: 7px;
				}
			}

			.cta {
				margin: 10px 0 0;
			}
		}
	}
}

@media (max-width: 480px) {
	.room {
		.details-bar {
			.icons-group {
				flex-direction: column;
			}
		}
	}
}
</style>
